import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  IconButton,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import { nmsMenus } from "globals/configuration/nms";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import DefaultCell from "application/components/DefaultCell";

const Attributes = {
  formSize: "md",
  enableAddButton: false,
  columns: 2,
  modes: nmsMenus.map((item) => ({
    id: item.key,
    name: item.name,
    iconName: item.icon,
    url: item.url,
    description: item.description,
  })),
};

function tryParseJSON(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return value;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return value;
  }
}

function isObject(val) {
  return val && typeof val === "object" && !Array.isArray(val);
}

const HEADER_BG = "#202940";
const BODY_BG = "#1a2035";

const HIDDEN_KEYS = new Set(["id", "uuid", "macAddress"]);

const SYSTEM_INFO_SECTIONS = [
  "hardware",
  "os",
  "network",
  "software",
  "processes",
  "license",
  "services",
];

function normalizeSystemInfo(value) {
  // Accept:
  // - stringified JSON
  // - already-parsed JSON
  // Shapes seen:
  // 1) { data: { hardware, os, ... }, deviceData: { ... } }
  // 2) { hardware, os, ... }
  const parsed = tryParseJSON(value);
  if (!isObject(parsed)) return parsed;

  // If coming from API sample: systemInfoJson -> { data: {...}, deviceData: {...} }
  const inner = isObject(parsed.data) ? parsed.data : parsed;
  const deviceData = isObject(parsed.deviceData) ? parsed.deviceData : undefined;

  if (deviceData) {
    return { ...inner, deviceData };
  }
  return inner;
}

function isNonEmpty(val) {
  if (val === null || val === undefined) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (isObject(val)) return Object.keys(val).length > 0;
  if (typeof val === "string") return val.trim().length > 0;
  return true;
}

function TitleCase(s) {
  if (!s) return "";
  return String(s)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function DeviceDataSection({ value }) {
  const parsed = tryParseJSON(value);
  if (!isObject(parsed)) return <ValueRenderer value={value} />;

  // For deviceData.MacAddress, show only the primary (main) mac address.
  // Backend sometimes returns an array of MACs; we only want the first non-empty one.
  const normalized = { ...parsed };
  if (Array.isArray(normalized.MacAddress)) {
    const first = normalized.MacAddress.find((v) => {
      if (v === null || v === undefined) return false;
      return String(v).trim().length > 0;
    });
    normalized.MacAddress = first !== undefined ? [first] : [];
  }

  // API sample shape:
  // deviceData: { MacAddress: [...], IPAddress: [...], Username: [...] }
  // Show MacAddress first as requested.
  const preferredOrder = ["MacAddress", "IPAddress", "Username"];
  const keys = Object.keys(normalized);
  const orderedKeys = [
    ...preferredOrder.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !preferredOrder.includes(k)),
  ].filter((k) => isNonEmpty(normalized?.[k]));

  if (orderedKeys.length === 0) {
    return <Typography variant="body2">No device data available</Typography>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {orderedKeys.map((k, idx) => (
        <Box
          key={k}
          sx={(theme) => ({
            padding: theme.spacing(1.25, 1.5),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: BODY_BG,
          })}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: theme.spacing(1),
              marginBottom: theme.spacing(0.75),
            })}
          >
            <Typography
              variant="button"
              sx={(theme) => ({
                fontWeight: 700,
                textTransform: "none",
                color: theme.palette.common.white,
              })}
            >
              {k === "MacAddress" ? "MAC Address" : TitleCase(k)}
            </Typography>
            {idx === 0 && k === "MacAddress" && (
              <Chip
                size="small"
                label="Primary"
                sx={(theme) => ({
                  backgroundColor: "rgba(255,255,255,0.12)",
                  color: theme.palette.common.white,
                  fontWeight: 600,
                })}
              />
            )}
          </Box>

          <ValueRenderer value={normalized[k]} />
        </Box>
      ))}
    </Box>
  );
}

function SystemInfoSections({ value, darkMode }) {
  const parsed = normalizeSystemInfo(value);
  if (!isObject(parsed)) {
    return <ValueRenderer value={value} />;
  }

  // OS-agnostic: show known sections first, but also show any other keys we receive.
  const known = SYSTEM_INFO_SECTIONS.filter((k) => isNonEmpty(parsed?.[k])).map((k) => [k, parsed[k]]);
  const extras = Object.keys(parsed)
    .filter((k) => !SYSTEM_INFO_SECTIONS.includes(k))
    .filter((k) => isNonEmpty(parsed?.[k]))
    .map((k) => [k, parsed[k]]);

  // Ensure deviceData is shown first among extra sections.
  const entries = [
    ...known,
    ...extras.sort(([a], [b]) => (a === "deviceData" ? -1 : b === "deviceData" ? 1 : 0)),
  ];
  if (entries.length === 0) {
    return <Typography variant="body2">No system info available</Typography>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {entries.map(([k, v]) => (
        <Accordion
          key={k}
          sx={(theme) => ({
            boxShadow: "none",
            backgroundColor: BODY_BG,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            "&:before": { display: "none" },
          })}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={(theme) => ({
              alignItems: "center",
              minHeight: 44,
              "& .MuiAccordionSummary-content": { marginY: theme.spacing(1) },
              backgroundColor: HEADER_BG,
              "& .MuiAccordionSummary-expandIconWrapper": {
                color: theme.palette.common.white,
              },
            })}
          >
            <Typography
              variant="button"
              sx={(theme) => ({
                fontWeight: 600,
                textTransform: "none",
                color: theme.palette.common.white,
              })}
            >
              {TitleCase(k)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={(theme) => ({
              padding: theme.spacing(2),
              backgroundColor: BODY_BG,
              color: theme.palette.common.white,
            })}
          >
            {k === "deviceData" ? <DeviceDataSection value={v} /> : <ValueRenderer value={v} />}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function copyToClipboard(text) {
  if (!navigator?.clipboard) {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
    return;
  }
  navigator.clipboard.writeText(text);
}

function ArrayOfObjectsTable({ data }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <Typography variant="body2">No items</Typography>;
  }

  const getPreferredKeyOrder = React.useCallback((keysList) => {
    // Try to keep the most important fields first for known systemInfoJson sections.
    // Any unknown keys are appended (stable) to avoid losing data.
    const preferredOrders = [
      ["pid", "name", "cpu_percent", "mem_percent"],
      ["name", "status"],
      ["interface_name", "mac", "ipv4", "ipv6", "is_up"],
    ];

    const preferred = preferredOrders.find((order) => order.some((k) => keysList.includes(k)));
    if (!preferred) return keysList;

    const head = preferred.filter((k) => keysList.includes(k));
    const tail = keysList.filter((k) => !head.includes(k));
    return [...head, ...tail];
  }, []);

  // Instead of a wide table (which becomes unreadable for 2-field objects like services),
  // render each array item as a compact key/value list.
  // Example: services -> show `name` (label) on top and `status` below.
  const keys = Array.from(
    data.reduce((acc, item) => {
      if (isObject(item)) Object.keys(item).forEach((k) => acc.add(k));
      return acc;
    }, new Set())
  );

  const orderedKeys = getPreferredKeyOrder(keys);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {data.map((row, idx) => (
        <Box
          key={idx}
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            overflow: "hidden",
            backgroundColor: BODY_BG,
          })}
        >
          {orderedKeys.map((k, i) => (
            <Box
              key={`${k}-${idx}`}
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: "160px 1fr",
                columnGap: theme.spacing(1.5),
                padding: theme.spacing(1, 1.5),
                borderTop: i === 0 ? "none" : `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  i % 2 === 0 ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)",
              })}
            >
              <Typography
                variant="caption"
                sx={(theme) => ({
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  opacity: 0.9,
                  textTransform: "none",
                })}
              >
                {TitleCase(k)}
              </Typography>
              <Box sx={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                <ValueRenderer value={row?.[k] === "" ? "-" : row?.[k]} />
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

function ValueRenderer({ value }) {
  const parsed = tryParseJSON(value);
  if (parsed === null) return <Typography variant="body2">null</Typography>;
  const textColor = (theme) => theme.palette.common.white;

  if (parsed === "") {
    return (
      <Typography
        variant="body2"
        sx={(theme) => ({
          color: textColor(theme),
          opacity: 0.7,
        })}
      >
        -
      </Typography>
    );
  }
  if (isObject(parsed)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {Object.keys(parsed).map((k) => (
          <Box
            key={k}
            sx={(theme) => ({
              display: "grid",
              gridTemplateColumns: "160px 1fr",
              columnGap: theme.spacing(1.5),
              rowGap: theme.spacing(0.5),
              paddingY: theme.spacing(0.5),
              borderBottom: `1px solid ${theme.palette.divider}`,
              "&:last-of-type": { borderBottom: "none" },
            })}
          >
            <Typography
              variant="caption"
              sx={(theme) => ({
                fontWeight: 600,
                opacity: 0.85,
                color: textColor(theme),
              })}
            >
              {k}
            </Typography>
            <Box sx={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}>
              <ValueRenderer value={parsed[k]} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (Array.isArray(parsed)) {
    if (parsed.length > 0 && parsed.every((it) => isObject(it))) {
      return <ArrayOfObjectsTable data={parsed} />;
    }
    // Array of primitives (e.g. software list) - show as a readable list.
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {parsed.map((it, i) => (
          <Typography
            key={i}
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.common.white,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
              paddingY: 0.25,
              borderBottom:
                i === parsed.length - 1 ? "none" : `1px dashed rgba(255,255,255,0.25)`,
            })}
          >
            {String(it)}
          </Typography>
        ))}
      </Box>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={(theme) => ({
        color: textColor(theme),
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
      })}
    >
      {String(parsed)}
    </Typography>
  );
}

function HardwareComponentDetails({ value }) {
  const parsed = tryParseJSON(value);
  if (!isObject(parsed)) return <ValueRenderer value={value} />;

  // Use the same column definition as used by CTLBaseDialogForm.
  // That guarantees consistent fields and ordering.
  const cols = getColumns?.(() => {}, () => {}) || [];
  const accessors = cols
    .map((c) => c?.accessor)
    .filter((a) => typeof a === "string" && a && a !== "id");

  const seen = new Set();
  const orderedKeys = accessors.filter((a) => {
    if (seen.has(a)) return false;
    seen.add(a);
    return true;
  });

  // Show only keys from getColumns. If nothing matches, fall back to generic renderer.
  const hasAny = orderedKeys.some((k) => k in parsed);
  if (!hasAny) return <ValueRenderer value={parsed} />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {orderedKeys.map((k) => (
        <Box
          key={k}
          sx={(theme) => ({
            display: "grid",
            gridTemplateColumns: "220px 1fr",
            columnGap: theme.spacing(1.5),
            rowGap: theme.spacing(0.5),
            paddingY: theme.spacing(0.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
            "&:last-of-type": { borderBottom: "none" },
          })}
        >
          <Typography
            variant="caption"
            sx={(theme) => ({
              fontWeight: 700,
              opacity: 0.9,
              color: theme.palette.common.white,
              textTransform: "none",
            })}
          >
            {TitleCase(k)}
          </Typography>
          <Box sx={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}>
            <DefaultCell value={parsed?.[k] === "" ? "-" : parsed?.[k]} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function FullDetails({
  open,
  onClose,
  data = {},
  title = "Details",
  onEditExternalHardware,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const normalized = React.useMemo(() => {
    const out = { ...data };
    ["hardwareComponentJson", "systemInfoJson"].forEach((k) => {
      if (out && typeof out[k] === "string") {
        out[k] = tryParseJSON(out[k]);
      }
    });
    return out;
  }, [data]);

  const visibleEntries = React.useMemo(
    () => Object.entries(normalized).filter(([key]) => !HIDDEN_KEYS.has(key)),
    [normalized]
  );

  const getSectionLabel = React.useCallback((key) => {
    switch (key) {
      case "hardwareComponentJson":
        return "External Hardware Details";
      case "systemInfoJson":
        return "System information Details";
      default:
        return key;
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={(theme) => ({
        ...CTLDialogContent(theme, { darkMode }),
        "& .MuiDialog-paper": {
          borderRadius: theme.shape.borderRadius * 1.5,
          overflow: "hidden",
        },
      })}
    >
      <DialogTitle
        sx={(theme) => ({
          ...CTLDialogContent(theme, { darkMode }),
          position: "relative",
          paddingRight: theme.spacing(9),
          backgroundColor: HEADER_BG,
        })}
      >
        <MDBox
          sx={(theme) => ({
            fontWeight: 600,
            color: theme.palette.common.white,
          })}
        >
          {title}
        </MDBox>

        <Box
          sx={(theme) => ({
            position: "absolute",
            right: theme.spacing(1),
            top: theme.spacing(1),
            display: "flex",
            gap: theme.spacing(1),
          })}
        >
          <Tooltip title="Copy raw JSON">
            <IconButton
              size="small"
              onClick={() => copyToClipboard(JSON.stringify(normalized, null, 2))}
              color="primary"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <IconButton
            size="small"
            onClick={onClose}
            color="primary"
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={(theme) => ({
          ...CTLDialogContent(theme, { darkMode }),
          padding: theme.spacing(2),
          backgroundColor: BODY_BG,
        })}
        dividers
      >
        <Grid container spacing={2}>
          {visibleEntries.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2">No details available</Typography>
            </Grid>
          )}

          {visibleEntries.map(([key, value]) => (
            <Grid
              item
              xs={12}
              md={key === "hardwareComponentJson" || key === "systemInfoJson" ? 12 : 6}
              key={key}
            >
              <Accordion
                defaultExpanded
                sx={(theme) => ({
                  boxShadow: "none",
                  backgroundColor: BODY_BG,
                  borderRadius: theme.shape.borderRadius,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                  "&:before": { display: "none" },
                })}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={(theme) => ({
                    alignItems: "center",
                    minHeight: 44,
                    "& .MuiAccordionSummary-content": {
                      marginY: theme.spacing(1),
                    },
                    backgroundColor: HEADER_BG,
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      color: theme.palette.common.white,
                    },
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: theme.spacing(1),
                      pr: theme.spacing(1),
                    })}
                  >
                    <Typography
                      variant="button"
                      sx={(theme) => ({
                        fontWeight: 600,
                        textTransform: "none",
                        color: theme.palette.common.white,
                      })}
                    >
                      {getSectionLabel(key)}
                    </Typography>

                    {key === "hardwareComponentJson" && typeof onEditExternalHardware === "function" && (
                      <Tooltip title="Edit external hardware details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditExternalHardware({
                              ...(data || {}),
                              hardwareComponentJson: value,
                            });
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={(theme) => ({
                    padding: theme.spacing(2),
                    backgroundColor: BODY_BG,
                    color: theme.palette.common.white,
                  })}
                >
                  {key === "systemInfoJson" ? (
                    <SystemInfoSections value={value} darkMode={darkMode} />
                  ) : key === "hardwareComponentJson" ? (
                    <HardwareComponentDetails value={value} />
                  ) : (
                    <ValueRenderer value={value} />
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

const getColumns = (onDelete, onEdit) => {
  return [
    {
      Header: "Action",
      accessor: "id",
      displayInList: true,
      allowInForm: false,
      Cell: ({ row, value }) => {
        return (
          <Box>
            <Typography variant="body2">{value}</Typography>
          </Box>
        );
      },
    },
  ];
};

export { getColumns, Attributes };
